import { Component, type ErrorInfo, type ReactNode } from 'react';
import { trackError } from '../utils/telemetry';

interface State {
  error: Error | null;
  info: ErrorInfo | null;
}

// Catches render-phase errors anywhere below it (incl. React min-error #185 =
// "Maximum update depth exceeded") so the screen doesn't go blank. Shows the
// real message + stack so prod crashes are diagnosable. Source maps must be
// enabled in vite.config for the stack to point at real source positions.
export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null, info: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] render crash:', error, info);
    // I crash di render intercettati qui non arrivano a window.onerror:
    // li inviamo esplicitamente alla telemetria.
    trackError(`[render] ${error.message}`, `${error.stack ?? ''}\n${info.componentStack ?? ''}`, window.location.href);
    this.setState({ info });
  }

  private reset = () => this.setState({ error: null, info: null });

  render(): ReactNode {
    if (!this.state.error) return this.props.children;

    return (
      <div
        style={{
          padding: 24, minHeight: '100vh', background: '#1a0a0a',
          color: '#eee', fontFamily: 'monospace', overflow: 'auto',
        }}
      >
        <h2 style={{ color: '#e57373', marginTop: 0 }}>Render error</h2>
        <p style={{ color: '#ffb703', fontSize: 14 }}>
          {this.state.error.message}
        </p>

        <h3 style={{ color: '#bbb', fontSize: 12, marginBottom: 4 }}>Stack</h3>
        <pre style={{
          fontSize: 11, background: '#000', padding: 12, borderRadius: 4,
          overflow: 'auto', whiteSpace: 'pre-wrap',
        }}>
          {this.state.error.stack}
        </pre>

        {this.state.info?.componentStack && (
          <>
            <h3 style={{ color: '#bbb', fontSize: 12, marginBottom: 4 }}>Component stack</h3>
            <pre style={{
              fontSize: 11, background: '#000', padding: 12, borderRadius: 4,
              overflow: 'auto', whiteSpace: 'pre-wrap',
            }}>
              {this.state.info.componentStack}
            </pre>
          </>
        )}

        <div style={{ marginTop: 16 }}>
          <button
            onClick={this.reset}
            style={{
              background: 'var(--accent-color)', color: '#000', padding: '6px 14px',
              border: 'none', borderRadius: 4, marginRight: 8, cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >Try again</button>
          <button
            onClick={() => { this.reset(); window.dispatchEvent(new Event('app-reset')); }}
            style={{
              background: 'none', color: '#ccc', padding: '6px 14px',
              border: '1px solid #555', borderRadius: 4, cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >Back to Lobby</button>
        </div>
      </div>
    );
  }
}
